import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faPen } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { startCase } from "lodash";

import { replaceLinks } from "~/feedback/utils";
import { prompt } from "~/lib/prompts";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import type { StreamSettingsResponseStream } from "~/stream/StreamSettings/index";
import { toast } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";

import type { DetailsStreamUpdateMutation as UpdateMutationType } from "~/__generated__/DetailsStreamUpdateMutation.graphql";

import styles from "./Details.module.css";

type User = NonNullable<StreamSettingsResponseStream>["otherUser"];

const UpdateMutation = graphql`
	mutation DetailsStreamUpdateMutation($input: UpdateNoteStreamInfoInput!) {
		updateNotestreamInfo(input: $input) {
			edge {
				node {
					id
					displayName
					type
					description
				}
			}
		}
	}
`;

export function renameStreamPrompt(streamId: string, title: string, callback?: () => void) {
	prompt("Series name", {
		initialInputValue: title,
		title: "Edit series name",
		inputPlaceholder: "Name this series… (max 255 chars)",
		yesText: "Save & Close",
		className: styles.editPrompt,
	}).then(val => {
		if (!val || val == title) {
			return;
		}

		commitMutation<UpdateMutationType>(environment, {
			mutation: UpdateMutation,
			variables: {
				input: {
					notestreamId: streamId,
					name: val as string,
				},
			},
			onCompleted: ({ updateNotestreamInfo }, errors) => {
				if (errors?.length || !updateNotestreamInfo?.edge?.node) {
					toast.error(errors?.[0]?.message || "Unable to update series title");
				} else {
					const stream = updateNotestreamInfo.edge.node;
					dispatchAnalyticsTrack(`${startCase(stream.type)} Series Name Changed`, {});
					callback?.();
				}
			},
		});
	});
}

interface EditButtonProps {
	content: string;
	placeholder: string;
	onClick: () => void;
}

function EditButton({ content, placeholder, onClick }: EditButtonProps) {
	const [hoveringLink, setHoveringLink] = React.useState(false);

	const onMouseEnter = React.useCallback(() => setHoveringLink(true), []);
	const onMouseLeave = React.useCallback(() => setHoveringLink(false), []);
	const onAnchorClick = React.useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

	const anchorProps = { onMouseEnter, onMouseLeave, onClick: onAnchorClick };

	return (
		<button className={cx(styles.editButton, { [styles.hoveringLink]: hoveringLink })} onClick={onClick}>
			{content ? (
				<p className={styles.content}>{replaceLinks(content, anchorProps)}</p>
			) : (
				<p className={cx(styles.content, styles.placeholder)}>{placeholder}</p>
			)}
			<FontAwesomeIcon icon={faPen} className={styles.icon} />
		</button>
	);
}

function UserInfo({ user }: { user: NonNullable<User> }) {
	return (
		<>
			<p className={styles.label}>1-on-1 with:</p>
			<div className={styles.userInfo}>
				<Avatar user={user} width="1rem" link={false} className={styles.avatar} />
				<p className={styles.name}>{user.fullName}</p>
			</div>
		</>
	);
}

interface Props {
	streamId: string;
	title: string;
	user?: User;
}

export default function Details({ streamId, title, user }: Props) {
	const onChangeTitle = React.useCallback(() => renameStreamPrompt(streamId, title), [streamId, title]);

	return (
		<main className={styles.container}>
			{user ? (
				<UserInfo user={user} />
			) : (
				<>
					<p className={styles.label}>Series Name</p>
					<EditButton content={title} placeholder="Add a title…" onClick={onChangeTitle} />
				</>
			)}
		</main>
	);
}
